import { useEffect } from 'react';
import './login.style.scss';
import { observer } from 'mobx-react-lite';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import { Box, Button, TextField, Typography, Theme, CircularProgress, IconButton, Tooltip, Link, Card } from '@mui/material';
import { accountState } from '../my-account/account-state';
import { makeStyles, createStyles } from '@mui/styles';
import { CustomerLoading } from '../../component/loading/loading';
import { useHistory, useLocation } from 'react-router-dom';
import DialogComponent from '../../component/dialog/dialog.component';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { DefaultWebsocketClient } from '../../modules/websocket/module';
import TranslatePopover from '../../component/popover/translate-popover';
import React from 'react';
import { GetThemeMode } from '../../themes/tool';
import { COLOR_KEY, getModeColor } from '../../themes/customization.value';
import PasswordInput from '../../component/form/password-input';
import PrivacyApgreementText from './privacy-apgreement-text';
import LanguageSwitcher from '../../component/language/language-switcher.component';
import { ReactComponent as LightMode } from '../../static/images/svg/akar-icons_sun-fill.svg';
import { ReactComponent as DarkMode } from '../../static/images/svg/akar-icons_moon-fill.svg';
/** 浅色主题下使用的品牌 Logo（与移动端 SMARTWAY-black 一致，深色图形） */
import { ReactComponent as LogoLight } from '../../static/images/svg/SMARTWAY-light.svg';
/** 深色主题下使用的品牌 Logo（与移动端 SMARTWAY-white 一致，浅色图形） */
import { ReactComponent as LogoDark } from '../../static/images/svg/SMARTWAY-dark.svg';
import AsyncCheckboxWithLoading from '../../component/async-checkbox-with-loading';
import { useTranslation } from 'react-i18next';
import { PermissionManager } from '../../router/permission.manager';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        'controll-panel': {
            [theme.breakpoints.down('sm')]: {
                maxWidth: '100%'
            },
            [theme.breakpoints.up('md')]: {
                maxWidth: 450
            },
            [theme.breakpoints.up('lg')]: {
                maxWidth: 600
            },
            [theme.breakpoints.up('xl')]: {
                maxWidth: 750
            },
        },
        'left-picture': {
            [theme.breakpoints.down('sm')]: {
                flex: 0
            },
        },
    }),
);

/**
 * 登录页面
 */
const LoginPage = observer(() => {
    // 根据当前输入的手机号码和缓存的用户的手机号码是否一致来判断是否是上次登录的用户
    // const isSame = (`${userInfo.employee_id}` === account);
    const location = useLocation()
    const history = useHistory();
    const adminOrStaff = location.pathname;
    useTranslation()

    /** 当前登录入口类型（/admin、/staff、/reseller），随路由切换同步 */
    const portalType = PermissionManager.getAccountTypeFromLoginPath(location.pathname);

    useEffect(() => {
        const currentPortalType = PermissionManager.getAccountTypeFromLoginPath(location.pathname);
        if (currentPortalType) {
            accountState.updateAccountType(currentPortalType);
        }
        if (accountState.getFromLocalStore()) {
            (async () => {
                const result = await accountState.checkAutoLogin(currentPortalType);
                if (result) {
                    history.push(PermissionManager.getDefaultHomePath(accountState.accountType));
                }
            })();
        }
    }, [location.pathname, history]);
    const [translateAnchorEl, setTranslateAnchorEl] = React.useState<HTMLElement | null>(null);
    const [agreedToPrivacy, setAgreedToPrivacy] = React.useState(false);
    const [privacyDialogOpen, setPrivacyDialogOpen] = React.useState(false);
    const needPrivacy = adminOrStaff === '/staff';

    /**
     * 登录前校验：先账号密码 → 再隐私勾选 → 调用 onLogin。
     * 供登录按钮与密码框回车复用。
     */
    const handleLoginAttempt = async () => {
        const phone = accountState.account.trim();
        const password = accountState.password.trim();
        if (!phone || !password) {
            GlobalNotification.error(i18n.t('login_page_alert_enter_phone_password'));
            return;
        }
        if (needPrivacy && !agreedToPrivacy) {
            setPrivacyDialogOpen(true);
            return;
        }
        await accountState.onLogin(portalType);
    };

    const classes = useStyles();
    const themeMode = GetThemeMode();
    const getColor = (key: COLOR_KEY) => getModeColor(key, themeMode);

    return (
        <div className='loginPage'>
            {/* 国际化选择器、主题切换器 */}
            <Box className='header' sx={{ color: getColor(COLOR_KEY.textPrimary300) }}>
                <LanguageSwitcher />
                <Tooltip title={i18n.t('general_text_switch_theme')} >
                    <IconButton
                        color='primary'
                        onClick={() => {
                            DefaultWebsocketClient.emit('SWITCH_THEME')
                        }}>
                        {themeMode === 'dark' ? <LightMode /> : <DarkMode />}
                    </IconButton>
                </Tooltip>
            </Box>
            {/* 登录框 */}
            <Card className={`controll-panel ${classes['controll-panel']}`}>
                {themeMode === 'light' ? (
                    <LogoDark width={180} height={120} aria-hidden />
                ) : (
                    <LogoLight width={180} height={120} aria-hidden />
                )}
                <Box className='title-box'>
                    <Typography
                        variant='h4'
                        className='title'
                    >
                        {i18n.t('general_text_project_title')}
                    </Typography>

                </Box>
                <div className='account'>
                    <div className='phone-country'>
                        <PhoneInput
                            enableSearch={true}
                            preferredCountries={['it', 'es', 'cn']}
                            containerClass='react-input-fixed'
                            inputStyle={{
                                display: 'none'
                            }}
                            containerStyle={{
                                width: 35,
                                height: 25,
                            }}
                            searchStyle={{
                                border: 'none',
                                outline: 'none',
                            }}
                            disableSearchIcon={true}
                            searchNotFound={i18n.t('general_text_not_found')}
                            value={accountState.phonePrefix || '39'}
                            onChange={(value) => accountState.changePhonePrefix(value)}
                        />
                    </div>
                    <TextField
                        className='account-input'
                        type='phone'
                        InputLabelProps={{
                            shrink: true
                        }}
                        value={accountState.account}
                        variant='outlined'
                        placeholder={i18n.t('general_text_account')}
                        onChange={(event) => {
                            accountState.changeAccount(event.target.value)
                        }}
                        fullWidth
                    />
                </div>
                <PasswordInput
                    value={accountState.password}
                    placeholder={i18n.t('login_page_text_password')}
                    onChange={(value) => {
                        accountState.changePassword(value)
                    }}
                    // 回车键入登录
                    onEnterLogin={handleLoginAttempt}
                />
                {needPrivacy && (
                    <Box sx={{ mt: 4 }}>
                        <AsyncCheckboxWithLoading
                            checked={agreedToPrivacy}
                            onChange={(value) => setAgreedToPrivacy(value)}
                            label={
                                <Typography variant='body1'>
                                    {i18n.t('login_page_text_agree_to')}
                                    <Link
                                        component='button'
                                        variant='body1'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPrivacyDialogOpen(true);
                                        }}
                                        sx={{ ml: 0.5 }}
                                        underline='none'
                                    >
                                        {i18n.t('login_page_text_privacy_policy')}
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                )}
                <Button
                    type='submit'
                    className='login-button'
                    variant='contained'
                    color={'primary'}
                    disabled={accountState.isLoading}
                    onClick={handleLoginAttempt}
                    fullWidth
                >
                    {i18n.t('login_page_button_login')}
                    {accountState.isLoading &&
                        <CustomerLoading />}
                </Button>

            </Card>
            {/* 页脚 */}
            <div className='footer'>
                <Typography
                    variant='subtitle1'
                    className='authorText'
                >
                    2025 MAIN TANK SRL. ALL RIGHTS RESERVED
                </Typography>
            </div>
            <DialogComponent open={accountState.autoLoginDialog.open}
                maxWidth={'md'}
                fullWidth={false}
                defaultHeaderProps={{ title: i18n.t('general_text_tips') }}
                onClose={() => accountState.closeAutoLoginDialog()}
            >
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} flexDirection={'row'}>
                    <CircularProgress size={'2rem'} />
                    <Typography variant='body1' style={{ marginLeft: '1rem' }}>
                        {i18n.t('general_text_automatic_login_ in_progress')}
                    </Typography>
                </Box>
            </DialogComponent>
            <TranslatePopover
                anchorEl={translateAnchorEl}
                onClose={() => {
                    setTranslateAnchorEl(null);
                }}
            />
            {needPrivacy && (
                <DialogComponent
                    open={privacyDialogOpen}
                    maxWidth={'lg'}
                    fullWidth={true}
                    defaultHeaderProps={{ title: i18n.t('login_page_text_privacy_policy') }}
                    onClose={() => setPrivacyDialogOpen(false)}
                    footer={(<Button
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            setAgreedToPrivacy(true);
                            setPrivacyDialogOpen(false);
                        }}
                    >
                        {i18n.t('login_page_button_agree')}
                    </Button>)}
                >
                    <Card sx={{ p: 2, borderRadius: 2 }}>
                        <PrivacyApgreementText />
                    </Card>
                </DialogComponent>
            )}
        </div>
    )
})

export default LoginPage